import { isLoading } from "apollo/utils";
import {
  BACKEND,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { decodeHtmlEntities } from "core/model/utils/strings";
import { QueryProgress } from "core/types";
import { Banner, Spinner } from "ds/ui";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";
import { DeletedSourceBanner } from "./DeletedSourceBanner";

type TranscriptSegment = {
  speaker?: string | null;
  text?: string | null;
};

type TranscriptData = {
  language?: string | null;
  segments: TranscriptSegment[];
};

function isTranscriptData(data: unknown): data is TranscriptData {
  return (
    typeof data === "object" &&
    data !== null &&
    "segments" in data &&
    Array.isArray((data as TranscriptData).segments)
  );
}

function SegmentsViewer({ data }: { data: TranscriptData }) {
  return (
    <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
      {data.segments.map((segment, index) => (
        <div key={index} className="flex flex-col gap-0.5">
          {segment.speaker ? (
            <span className="text-body-sm font-bold text-gray-600">
              {segment.speaker}
            </span>
          ) : null}
          <p className="text-body-md whitespace-pre-wrap">
            {decodeHtmlEntities(segment.text ?? "")}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a voice transcript from its processed document URI. Consultation
 * transcripts arrive as diarised JSON segments; dictation transcripts arrive as
 * a formatted markdown/plain-text transcript. Both are read-only.
 */
export function VoiceTranscriptViewer({
  isDeleted,
  url,
}: {
  url: string;
  /** Set when the underlying AI-core document has been deleted; skips fetching. */
  isDeleted?: boolean;
}) {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();
  const [transcript, setTranscript] = useState<TranscriptData | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  useEffect(() => {
    if (isDeleted || !url) {
      setTranscript(null);
      setRawText(null);
      setProgress(QUERY_PROGRESS_NOT_STARTED);
      return;
    }

    const controller = new AbortController();
    setProgress(QUERY_PROGRESS_PENDING);
    setTranscript(null);
    setRawText(null);

    const isAiCoreHostedFile =
      !!aiCoreConfig?.host && url.includes(aiCoreConfig.host);
    const headers: HeadersInit = {};
    if (isAiCoreHostedFile && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    (async () => {
      try {
        const res = await fetch(url, {
          headers,
          signal: controller.signal,
        });

        if (!res.ok) {
          console.error(
            "No fetch response in VoiceTranscriptViewer:",
            `${res.status} ${res.statusText}`,
          );
          setProgress(QUERY_PROGRESS_FAILED);
          return;
        }

        const text = await res.text();
        if (controller.signal.aborted) return;

        try {
          const parsed = JSON.parse(text);
          if (isTranscriptData(parsed)) {
            setTranscript(parsed);
          } else if (typeof parsed === "string") {
            setRawText(decodeHtmlEntities(parsed));
          } else {
            setRawText(JSON.stringify(parsed, null, 2));
          }
        } catch {
          setRawText(decodeHtmlEntities(text));
        }
        setProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching voice transcript:", error);
        setProgress(QUERY_PROGRESS_FAILED);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [isDeleted, url, aiCoreConfig?.host, token]);

  if (isDeleted) {
    return <DeletedSourceBanner />;
  }

  if (progress === QUERY_PROGRESS_FAILED) {
    return (
      <Banner
        color="danger"
        message={translations.extractDocs.splitView.sourcesTab.errorNoHisData}
      />
    );
  }

  if (isLoading(progress)) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <Spinner
          color="primary"
          size="lg"
          testId="spinner-loading-voice-transcript"
        />
      </div>
    );
  }

  if (transcript) {
    return <SegmentsViewer data={transcript} />;
  }

  return (
    <pre className="overflow-auto px-6 py-4 text-left font-sans text-body-md whitespace-pre-wrap">
      {rawText ?? ""}
    </pre>
  );
}
